import React, { useState, useEffect } from 'react';
import PublicLayout from '../components/PublicLayout';
import { useTranslation } from 'react-i18next';
import { ContactSEO } from '../components/SEO';
import { Send, Loader2, CheckCircle, Mail, MessageSquare, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { FloatingShapes } from '../components/icons/FeatureIcons';

// Dynamically load reCAPTCHA script
const loadRecaptcha = (siteKey) => {
  return new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      resolve(window.grecaptcha);
      return;
    }
    
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.grecaptcha.ready(() => resolve(window.grecaptcha));
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const Contact = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});

  // Load reCAPTCHA on mount
  useEffect(() => {
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (siteKey) {
      loadRecaptcha(siteKey)
        .then(() => setRecaptchaLoaded(true))
        .catch(err => console.error('Failed to load reCAPTCHA:', err));
    }
    
    // Cleanup: hide badge when leaving the page
    return () => {
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.visibility = 'hidden';
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      newErrors.name = t('pages.contact.form.errors.nameRequired');
    }
    
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('pages.contact.form.errors.emailInvalid');
    }
    
    if (!formData.subject.trim() || formData.subject.trim().length < 3) {
      newErrors.subject = t('pages.contact.form.errors.subjectRequired');
    }
    
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      newErrors.message = t('pages.contact.form.errors.messageRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      // Execute reCAPTCHA v3 if configured
      const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      let recaptchaToken = null;
      
      if (recaptchaSiteKey && recaptchaLoaded) {
        try {
          const grecaptcha = window.grecaptcha;
          if (grecaptcha && grecaptcha.execute) {
            recaptchaToken = await grecaptcha.execute(recaptchaSiteKey, { action: 'contact' });
          }
        } catch (recaptchaError) {
          // reCAPTCHA failed (invalid key, etc.) - continue without token
          console.warn('reCAPTCHA execution failed, proceeding without token:', recaptchaError.message);
        }
      }

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptcha_token: recaptchaToken
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Map error codes to translation keys
        const errorCode = data.detail;
        if (errorCode === 'RECAPTCHA_FAILED') {
          throw new Error(t('pages.contact.form.errors.recaptchaFailed'));
        }
        throw new Error(t('pages.contact.form.errorMessage'));
      }

      setSubmitted(true);
      toast.success(t('pages.contact.form.successMessage'));
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || t('pages.contact.form.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PublicLayout>
        <ContactSEO />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{t('pages.contact.form.thankYouTitle')}</h1>
            <p className="text-lg text-gray-600">{t('pages.contact.form.thankYouMessage')}</p>
            <button
              onClick={() => {
                setSubmitted(false);
                setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
              }}
              className="inline-flex items-center px-6 py-3 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors"
            >
              {t('pages.contact.form.sendAnother')}
            </button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <ContactSEO />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-purple-600 to-indigo-700 py-16 overflow-hidden">
        <FloatingShapes />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <p className="text-sm font-semibold text-purple-200 uppercase tracking-wide mb-4">{t('footer.contact')}</p>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white mb-4">{t('pages.contact.title')}</h1>
          <p className="text-xl text-purple-100 max-w-2xl mx-auto">{t('pages.contact.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info Cards */}
          <div className="space-y-6">
            <div className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
              <p className="text-gray-600">info@idemenu.com</p>
            </div>
            
            <div className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Support</h3>
              <p className="text-gray-600">support@idemenu.com</p>
            </div>
            
            <div className="p-6 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Response Time</h3>
              <p className="text-gray-600">Within 24 hours</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-5">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('pages.contact.form.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors`}
                placeholder={t('pages.contact.form.namePlaceholder')}
              />
              {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('pages.contact.form.email')} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors`}
                placeholder={t('pages.contact.form.emailPlaceholder')}
              />
              {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
            </div>

            {/* Phone Field (Optional) */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                {t('pages.contact.form.phone')} <span className="text-gray-400 text-xs">({t('pages.contact.form.optional')})</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                placeholder={t('pages.contact.form.phonePlaceholder')}
              />
            </div>

            {/* Subject Field */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                {t('pages.contact.form.subject')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg border ${errors.subject ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors`}
                placeholder={t('pages.contact.form.subjectPlaceholder')}
              />
              {errors.subject && <p className="mt-1 text-sm text-red-500">{errors.subject}</p>}
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                {t('pages.contact.form.message')} <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-lg border ${errors.message ? 'border-red-500' : 'border-gray-300'} focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors resize-none`}
                placeholder={t('pages.contact.form.messagePlaceholder')}
              />
              {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('pages.contact.form.sending')}
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  {t('pages.contact.form.submit')}
                </>
              )}
            </button>
          </div>

              {/* reCAPTCHA Notice */}
              <p className="text-xs text-gray-500 text-center">
                {t('pages.contact.form.recaptchaNotice')}
              </p>
            </form>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default Contact;
